import { useCallback, useState } from 'react'
import MaskedInput from 'react-text-mask'
import { toast } from 'react-toastify'
import styled from 'styled-components/macro'

import { Layout, Dialog, DatePicker, Label, TextField } from '@loadsmart/loadsmart-ui'
import type EventLike from '@loadsmart/loadsmart-ui/dist/utils/types/EventLike'
import { Text, Button } from '@loadsmart/miranda-react'

import { getPreviousDateTime } from '../presenters/stopPresenter'
import { useShipment } from './hooks/useShipment'

const StyledDialogBody = styled(Dialog.Body)`
  margin-bottom: 0;
`

const StyledGrid = styled(Layout.Grid)`
  grid-template-columns: 50% 50%;
`

type ShipmentStopCheckDialogProps = {
  shipmentUUID: string
  stopId: string | number
  previousDateTime: string
  isOpen: boolean
  hideDialog: () => void
  callback: () => void
  stopType: string
  isLoading: boolean
  setIsLoading: (isLoading: boolean) => void
}

export default function StopCheckDialog({
  shipmentUUID,
  stopId,
  previousDateTime,
  isOpen,
  hideDialog,
  callback,
  stopType,
  isLoading,
  setIsLoading,
}: ShipmentStopCheckDialogProps) {
  const { submitShipmentStatus } = useShipment()

  const previousDate = getPreviousDateTime(previousDateTime, "yyyy-MM-dd'T'HH:mm:ss")
  const previousTime = getPreviousDateTime(previousDateTime, 'HH:mm')

  const [date, setDate] = useState<string | null | undefined>(previousDate)
  const [time, setTime] = useState<string | null | undefined>(previousTime)

  const handleUpdate = useCallback(async () => {
    if (!date || !time) return

    setIsLoading(true)

    try {
      await submitShipmentStatus({
        shipmentUUID,
        stopId,
        type: `check-${stopType}`,
        date,
        time,
      })

      toast.success('Shipment updated successfully')

      callback()
      hideDialog()
    } catch {
      setIsLoading(false)
      toast.error('Could not update shipment, try again later')
    }
  }, [
    callback,
    date,
    hideDialog,
    setIsLoading,
    shipmentUUID,
    stopId,
    stopType,
    submitShipmentStatus,
    time,
  ])

  const handleDateChange = useCallback((event: EventLike<string | null>) => {
    setDate(event.target.value)
  }, [])

  const handleTimeChange = useCallback((event: EventLike<string | null>) => {
    setTime(event.target.value)
  }, [])

  return (
    <Dialog open={isOpen} onOverlayClick={hideDialog} scale="small">
      <Dialog.Header>Confirm check-{stopType}</Dialog.Header>
      <StyledDialogBody>
        <Layout.Stack space="l">
          <StyledGrid>
            <Layout.Stack space="xs">
              <Label htmlFor="date-picker">
                <Text color="color-text-tertiary">Date</Text>
              </Label>
              <DatePicker
                id="date-picker"
                name="date-picker"
                onChange={handleDateChange}
                value={date}
              />
            </Layout.Stack>
            <Layout.Stack space="xs">
              <Label htmlFor="time">
                <Text color="color-text-tertiary">Time (24-hour)</Text>
              </Label>
              <MaskedInput
                id="time"
                name="time"
                mask={[/[0-2]/, /\d/, ':', /[0-5]/, /\d/]}
                placeholder="00:00"
                aria-required="true"
                onChange={handleTimeChange}
                value={time || undefined}
                render={(ref: (inputElement: HTMLInputElement) => void, props) => (
                  <TextField ref={ref} {...props} />
                )}
              />
            </Layout.Stack>
          </StyledGrid>
          <Text variant="body-md" color="color-text-primary">
            ATTENTION: Set date and time to match driver's timezone.
          </Text>
          <Button
            type="button"
            variant="primary"
            style={{ display: 'grid' }}
            onClick={handleUpdate}
            disabled={!date || !time || isLoading}
            loading={isLoading}
          >
            Confirm driver's {stopType === 'in' ? 'arrival ' : 'departure'}
          </Button>
        </Layout.Stack>
      </StyledDialogBody>
    </Dialog>
  )
}
